/* jshint esversion: 6 */
//
// Drives the "Resend Confirmation" / "Resend Confirmations" actions for both
// per-row (side panel "..." menu) and bulk (Track action bar) entry points.
//
// Flow:
//   1. preventDefault on the trigger
//   2. POST endpoint with dry_run=true to compute eligibility for the
//      selected list_item_ids
//   3. show a sweetAlert preview modal: "X eligible, Y skipped"
//   4. on confirm, POST endpoint with dry_run=false
//   5. show a result modal: "Queued: X · Skipped: Y" + per-reason breakdown
//
// Backend contract (POST):
//   - param list_item_ids[]: array of integers
//   - param dry_run: "true" / "false"
//   - response 200: { queued_count, skipped_count, would_queue_count,
//                     results: [{ list_item_id, decision, reason,
//                                 classified_state }] }
//   - response 422: { error: "too_many_attendees", max: N }
//
// The selected-row source for bulk mode is the existing hidden-input
// rendering produced by datatables/table-setup.js (selector
// `input[name="event_mailing[list_item_ids][]"]` within the action-bar
// flash wrapper — see app/javascript/datatables/table-setup.js).
import { Controller } from 'stimulus';
import sweetAlert from 'sweetalert';
import I18n from '../i18n-js/index.js.erb';

const t = (key, opts) => I18n.t(key, {
  scope: 'js.my_events.confirmation_resends',
  ...opts
});
const tShared = (key, opts) => I18n.t(key, {
  scope: 'shared',
  ...opts
});

export default class extends Controller {
  static values = {
    url: String,
    // For single-row triggers only. Bulk reads from hidden inputs at
    // click time so it stays in sync with the current selection.
    listItemId: Number
  };

  // Wired via data-action="click->confirmation-resend#openSingle"
  openSingle(event) {
    event.preventDefault();
    const id = this.listItemIdValue;
    if (!id) {
      return;
    }
    this._run([id], { mode: 'single' });
  }

  // Wired via data-action="click->confirmation-resend#openBulk"
  openBulk(event) {
    event.preventDefault();
    const ids = this._readSelectedIds();
    if (ids.length === 0) {
      return;
    }
    this._run(ids, { mode: 'bulk' });
  }

  // --- private ---------------------------------------------------------

  _readSelectedIds() {
    // Datatables renders hidden inputs into .js-selected-rows-ids within
    // the action-bar-flash-wrapper. We scope to the closest wrapper so a
    // page with multiple lists (e.g. Activity-grouped Track) reads the
    // right selection.
    const wrapper = this.element.closest('.js-action-bar-flash-wrapper') ||
                    document.querySelector('.js-action-bar-flash-wrapper');
    if (!wrapper) {
      return [];
    }
    return Array.from(
      wrapper.querySelectorAll('input[type="hidden"][name="event_mailing[list_item_ids][]"]')
    ).map((el) => Number(el.value)).filter(Number.isFinite);
  }

  async _run(listItemIds, { mode }) {
    let preview;
    try {
      preview = await this._postJSON({ list_item_ids: listItemIds, dry_run: true });
    } catch (err) {
      this._showError(err);
      return;
    }

    const confirmed = await this._showPreview(preview, { mode, total: listItemIds.length });
    if (!confirmed) {
      return;
    }

    // If the dry run said nothing was eligible, don't make the real call.
    if ((preview.would_queue_count || 0) === 0) {
      return;
    }

    let result;
    try {
      result = await this._postJSON({ list_item_ids: listItemIds, dry_run: false });
    } catch (err) {
      this._showError(err);
      return;
    }

    this._showResult(result);
  }

  async _postJSON(body) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch(this.urlValue, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken || ''
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const err = new Error(payload.error || `HTTP ${response.status}`);
      err.payload = payload;
      err.status = response.status;
      throw err;
    }
    return response.json();
  }

  _showPreview(preview, { mode, total }) {
    const wouldQueue = preview.would_queue_count || 0;
    const skipped = preview.skipped_count || 0;

    if (mode === 'single') {
      return this._showSinglePreview(preview, { wouldQueue, skipped });
    }
    return this._showBulkPreview(preview, { total, wouldQueue, skipped });
  }

  _showSinglePreview(preview, { wouldQueue, skipped }) {
    // For a single attendee we either show the confirmation prompt or the
    // ineligible-with-reason explainer. The skip reason comes back keyed.
    if (wouldQueue === 1) {
      return sweetAlert({
        title: t('preview.single.title'),
        text: t('preview.single.body'),
        icon: 'info',
        buttons: [tShared('buttons.cancel'), t('preview.confirm_button')]
      });
    }

    const reason = (preview.results || [])[0]?.reason || 'unknown';
    return sweetAlert({
      title: t('preview.ineligible.title'),
      text: t(`reasons.${reason}`, { defaultValue: t('reasons.unknown') }),
      icon: 'warning',
      buttons: { confirm: { text: tShared('buttons.close'), value: false } }
    });
  }

  _showBulkPreview(preview, { total, wouldQueue, skipped }) {
    const summary = t('preview.bulk.summary', {
      total: total,
      eligible: wouldQueue,
      skipped: skipped
    });

    const breakdown = this._breakdown(preview.results || [], 'skipped');
    const breakdownText = breakdown.length > 0
      ? `\n\n${t('preview.bulk.skipped_breakdown')}\n${breakdown.join('\n')}`
      : '';

    if (wouldQueue === 0) {
      return sweetAlert({
        title: t('preview.bulk.none_eligible_title'),
        text: t('preview.bulk.none_eligible_body') + breakdownText,
        icon: 'warning',
        buttons: { confirm: { text: tShared('buttons.close'), value: false } }
      });
    }

    return sweetAlert({
      title: t('preview.bulk.title'),
      text: t('preview.bulk.body') + '\n\n' + summary + breakdownText,
      icon: 'info',
      buttons: [tShared('buttons.cancel'), t('preview.confirm_button')]
    });
  }

  _showResult(result) {
    const queued = result.queued_count || 0;
    const skipped = result.skipped_count || 0;
    const summary = t('result.summary', { queued: queued, skipped: skipped });
    const breakdown = this._breakdown(result.results || [], 'skipped');
    const breakdownText = breakdown.length > 0
      ? `\n\n${t('result.skipped_breakdown')}\n${breakdown.join('\n')}`
      : '';
    sweetAlert({
      title: t('result.title'),
      text: summary + breakdownText,
      icon: queued > 0 ? 'success' : 'warning',
      button: { text: tShared('buttons.close'), value: true }
    });
  }

  _breakdown(results, decision) {
    const filtered = results.filter((r) => r.decision === decision);
    if (filtered.length === 0) {
      return [];
    }
    const counts = filtered.reduce((acc, r) => {
      acc[r.reason] = (acc[r.reason] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([reason, count]) => {
      const reasonText = t(`reasons.${reason}`, { defaultValue: t('reasons.unknown') });
      return `• ${count} ${reasonText}`;
    });
  }

  _showError(err) {
    const isTooMany = err.payload && err.payload.error === 'too_many_attendees';
    sweetAlert({
      title: t(isTooMany ? 'errors.too_many.title' : 'errors.generic.title'),
      text: isTooMany
        ? t('errors.too_many.body', { max: err.payload.max })
        : t('errors.generic.body'),
      icon: 'error',
      button: { text: tShared('buttons.close'), value: true }
    });
  }
}
